/**
 * Polymorphic Preset manifest schema.
 *
 * A Preset is the unified composition + configuration primitive. Items can be:
 * - Asset ref (plain or with inline config + placeholders)
 * - Library Instance ref (`library://tier/instances/id`)
 * - Nested Preset ref (resolved recursively, max depth 3)
 *
 * Apply semantics derive from ref type, not an explicit mode field.
 */
import * as z from "zod";
import { type ManifestValidationResult, validateManifest } from "./_shared.js";

// ---------------------------------------------------------------------------
// Placeholder types
// ---------------------------------------------------------------------------

const PlaceholderTypeValues = [
  "text",
  "multiline",
  "secret",
  "number",
  "boolean",
  "enum",
  "oauth",
  "instance-picker",
] as const;

export const PlaceholderTypeSchema = z.enum(PlaceholderTypeValues);

export const PlaceholderSchema = z.object({
  key: z.string().min(1),
  prompt: z.string().optional(),
  type: PlaceholderTypeSchema.optional(),
  required: z.boolean().optional(),
  default: z.union([z.string(), z.number(), z.boolean()]).optional(),
  values: z.array(z.union([z.string(), z.number()])).optional(),
});

// ---------------------------------------------------------------------------
// Compose item (for agents within presets)
// ---------------------------------------------------------------------------

const ComposeRefSchema = z.object({
  kind: z.string().min(1),
  ref: z.string().min(1),
});

// ---------------------------------------------------------------------------
// Preset item
// ---------------------------------------------------------------------------

export const PresetItemSchema = z.looseObject({
  /** Asset ref, Library Instance ref, or nested Preset ref. */
  ref: z.string().min(1),
  /** Local item ID for cross-item references (#item-id). */
  id: z.string().optional(),
  /** Author-prefilled non-secret config values. */
  config: z.record(z.string(), z.unknown()).optional(),
  /** User/secret values prompted at install. */
  placeholders: z.array(PlaceholderSchema).optional(),
  /** How placeholder values fold into the Instance config. */
  materialize: z.record(z.string(), z.string()).optional(),
  /** Composition for agent items. */
  composes: z.array(ComposeRefSchema).optional(),
  /** Outer-Preset-author-supplied placeholder values for nested Presets. */
  inputs: z.record(z.string(), z.unknown()).optional(),
});

// ---------------------------------------------------------------------------
// Preset-level input (rare, shared across items)
// ---------------------------------------------------------------------------

const PresetInputSchema = z.object({
  key: z.string().min(1),
  type: PlaceholderTypeSchema.optional(),
  prompt: z.string().optional(),
  required: z.boolean().optional(),
  values: z.array(z.union([z.string(), z.number()])).optional(),
  default: z.union([z.string(), z.number(), z.boolean()]).optional(),
});

// ---------------------------------------------------------------------------
// Source (git repo backing non-factory dependencies)
// ---------------------------------------------------------------------------

/** A `{url, pin?}` source the preset's dependencies resolve from (mirrors `SourceEntry`). */
export const PresetSourceSchema = z.object({
  url: z.string().min(1),
  pin: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Preset manifest
// ---------------------------------------------------------------------------

export const PresetManifestSchema = z.looseObject({
  publisher: z.string().optional(),
  name: z.string().min(1),
  version: z.union([z.string(), z.number()]),
  description: z.string().min(1),
  category: z.string().optional(),
  license: z.string().optional(),
  keywords: z.array(z.string()).optional(),

  /** 1+ polymorphic items. */
  items: z.array(PresetItemSchema).min(1),

  /** Preset-level inputs shared across items (rare). */
  inputs: z.array(PresetInputSchema).optional(),

  /** Git sources the dependency items resolve from; `apply` registers these first. */
  sources: z.array(PresetSourceSchema).optional(),
});

export type PresetManifest = z.infer<typeof PresetManifestSchema>;
export type PresetItem = z.infer<typeof PresetItemSchema>;
export type PresetSource = z.infer<typeof PresetSourceSchema>;
export type Placeholder = z.infer<typeof PlaceholderSchema>;

export function validatePreset(data: unknown): ManifestValidationResult<PresetManifest> {
  return validateManifest(PresetManifestSchema, data);
}
